/**
 * Who is behind, by person.
 *
 * The same underlying records as the acknowledgements index, turned the other
 * way up. That index answers "which policy needs chasing"; this answers "who
 * do I talk to", which is what a manager actually does with the information.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useOutstandingAcks, type OutstandingAck } from './useAckReport';
import { formatDate } from '../policies/policyTable';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import styles from '../../shared/Page.module.css';
import own from './Admin.module.css';
import { FILTER_MAX } from '../../types/fieldLimits';

interface PersonRow {
  userId: string;
  userName: string;
  userEmail: string;
  overdue: number;
  pending: number;
  /** The deadline that passed longest ago. */
  oldestDue: string | null;
}

/** Roll outstanding records up per person, most overdue first. */
export function groupByPerson(acks: readonly OutstandingAck[]): PersonRow[] {
  const byUser = new Map<string, PersonRow>();

  for (const a of acks) {
    const row = byUser.get(a.userId) ?? {
      userId: a.userId,
      userName: a.userName,
      userEmail: a.userEmail,
      overdue: 0,
      pending: 0,
      oldestDue: null,
    };

    if (a.status === 'overdue') {
      row.overdue += 1;
      // Earliest missed deadline, which is how long this has been true.
      if (a.dueDate && (!row.oldestDue || a.dueDate < row.oldestDue)) {
        row.oldestDue = a.dueDate;
      }
    } else {
      row.pending += 1;
    }
    byUser.set(a.userId, row);
  }

  return [...byUser.values()].sort(
    (x, y) => y.overdue - x.overdue || y.pending - x.pending,
  );
}

export function OverduePage() {
  const { session } = useAuth();
  const { data, isLoading, error } = useOutstandingAcks(session?.orgId ?? null);
  const [query, setQuery] = useState('');

  const rows = useMemo(() => {
    // Only people with something actually late. "Pending and not yet due" is
    // not overdue, and putting it on this page would train people to ignore it.
    const late = (data ?? []).filter((a) => a.status === 'overdue');
    const grouped = groupByPerson(late);
    const q = query.trim().toLowerCase();
    if (!q) return grouped;
    return grouped.filter(
      (r) =>
        r.userName.toLowerCase().includes(q) || r.userEmail.toLowerCase().includes(q),
    );
  }, [data, query]);

  if (isLoading) return <Spinner label="Loading overdue acknowledgements" />;

  if (error) {
    return (
      <PageState title="Could not load this page" tone="alert">
        {error instanceof Error ? error.message : 'Something went wrong.'}
      </PageState>
    );
  }

  const totalLate = (data ?? []).filter((a) => a.status === 'overdue').length;

  return (
    <>
      <header className={styles.header}>
        <h1 className={styles.title}>Overdue</h1>
        <p className={styles.subtitle}>
          Staff with acknowledgements past their deadline. Each of these is
          something a surveyor could record as a finding today.
        </p>
      </header>

      <div className={styles.body}>
        {totalLate === 0 ? (
          <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">
            Nobody is overdue. Anything still pending is within its deadline —
            see <Link to="/admin/acknowledgements">Acknowledgements</Link> for
            what is outstanding.
          </div>
        ) : (
          <>
            <dl className={own.tiles}>
              <div className={own.tile}>
                <dt className={own.tileKey}>People overdue</dt>
                <dd className={`${own.tileValue} tnum ${own.danger}`}>{rows.length}</dd>
              </div>
              <div className={own.tile}>
                <dt className={own.tileKey}>Overdue acknowledgements</dt>
                <dd className={`${own.tileValue} tnum`}>{totalLate}</dd>
              </div>
            </dl>

            <div className={own.bar}>
              <div className={own.search}>
                <label className="vh" htmlFor="person-filter">
                  Filter people by name or email
                </label>
                <input
                  id="person-filter"
            maxLength={FILTER_MAX}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Filter by name or email"
                />
              </div>
              <div className={own.count} role="status" aria-live="polite">
                <b>{rows.length}</b> {rows.length === 1 ? 'person' : 'people'}
              </div>
            </div>

            <div className={own.scroller}>
              <table className={own.table}>
                <caption>Staff with overdue acknowledgements, most first</caption>
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Email</th>
                    <th scope="col" className={own.numeric}>Overdue</th>
                    <th scope="col">Since</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.userId}>
                      <td style={{ fontWeight: 500 }}>{r.userName}</td>
                      <td style={{ color: 'var(--ink-2)' }}>
                        <a href={`mailto:${r.userEmail}`} className={own.link}>
                          {r.userEmail}
                        </a>
                      </td>
                      <td className={`${own.numeric} tnum`}>
                        <span className={`${own.pill} ${styles.danger}`}>{r.overdue}</span>
                      </td>
                      <td className="tnum">
                        {r.oldestDue ? formatDate(r.oldestDue)
                          : <span className={styles.absent}>&mdash;</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {rows.length === 0 && (
              <p className={styles.state}>
                Nobody matches <strong>{query.trim()}</strong>.
              </p>
            )}
          </>
        )}
      </div>
    </>
  );
}
